import React, { useCallback, useEffect, useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Driver, DriverAssignmentMode } from '../models/Driver';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { driverService } from '../services/driverService';

interface PendingDriverRequestsPanelProps {
  onDriverAssigned?: () => Promise<void> | void;
  onPendingCountChange?: (count: number) => void;
}

const formatDriverTimestamp = (timestamp?: Driver['createdAt']): string => {
  const date = timestamp?.toDate?.();
  if (!date) {
    return 'Date inconnue';
  }
  return date.toLocaleString('fr-FR');
};

const matchesPendingDriverSearch = (
  driver: Driver,
  normalizedSearchQuery: string
): boolean => {
  if (!normalizedSearchQuery) {
    return true;
  }

  const searchableValues = [
    driver.firstName,
    driver.lastName,
    `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
    driver.phone,
    driver.email,
    driver.cin,
    driver.vehicleRegistration,
  ];

  return searchableValues.some((value) =>
    (value || '').toLowerCase().includes(normalizedSearchQuery)
  );
};

const getDriverAccountStatusMeta = (driver: Driver) => {
  if (driver.isSuspended || driver.approvalStatus === 'suspended') {
    return { label: 'Suspendu', color: 'error' as const };
  }

  if (driver.enabled) {
    return { label: 'Activé', color: 'success' as const };
  }

  if (driver.approvalStatus === 'approved') {
    return { label: 'Approuvé', color: 'info' as const };
  }

  return { label: 'En attente', color: 'warning' as const };
};

const PendingDriverRequestsPanel: React.FC<PendingDriverRequestsPanelProps> = ({
  onDriverAssigned,
  onPendingCountChange,
}) => {
  const { stores, operationGroups, selectedGroupId, selectedStoreId } =
    useAdminScope();
  const [pendingDrivers, setPendingDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [driverToAssign, setDriverToAssign] = useState<Driver | null>(null);
  const [assignmentStoreId, setAssignmentStoreId] = useState('');
  const [activateNow, setActivateNow] = useState(true);
  const [lockToStore, setLockToStore] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<Driver | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const assignableStores = stores.filter(
    (store) =>
      typeof store.operationGroupId === 'string' &&
      store.operationGroupId.trim().length > 0
  );
  const selectedAssignmentStore =
    assignableStores.find((store) => store.id === assignmentStoreId) || null;
  const selectedAssignmentGroup =
    operationGroups.find(
      (group) => group.id === selectedAssignmentStore?.operationGroupId
    ) || null;

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredPendingDrivers = pendingDrivers.filter((driver) =>
    matchesPendingDriverSearch(driver, normalizedSearchQuery)
  );
  const displayedPendingDrivers = filteredPendingDrivers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const loadPendingDrivers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const nextPendingDrivers = await driverService.getPendingDriverRequests();
      setPendingDrivers(nextPendingDrivers);
      onPendingCountChange?.(nextPendingDrivers.length);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des inscriptions.');
      console.error('Error fetching pending driver requests:', err);
    } finally {
      setLoading(false);
    }
  }, [onPendingCountChange]);

  useEffect(() => {
    void loadPendingDrivers();
  }, [loadPendingDrivers]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleChangePage = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resolveDefaultStoreId = (): string => {
    if (
      selectedStoreId &&
      assignableStores.some((store) => store.id === selectedStoreId)
    ) {
      return selectedStoreId;
    }

    if (selectedGroupId) {
      const preferredStore = assignableStores.find(
        (store) => store.operationGroupId === selectedGroupId
      );
      if (preferredStore) {
        return preferredStore.id;
      }
    }

    const firstActiveStore = assignableStores.find((store) => store.isActive);
    return firstActiveStore?.id || assignableStores[0]?.id || '';
  };

  const resetAssignmentDialogState = () => {
    setIsAssignDialogOpen(false);
    setDriverToAssign(null);
    setAssignmentStoreId('');
    setActivateNow(true);
    setLockToStore(false);
    setIsAssigning(false);
  };

  const handleOpenAssignDialog = (driver: Driver) => {
    setDriverToAssign(driver);
    setAssignmentStoreId(resolveDefaultStoreId());
    setActivateNow(true);
    setLockToStore(false);
    setError(null);
    setSuccessMessage(null);
    setIsAssignDialogOpen(true);
  };

  const getDriverLabel = (driver: Driver): string =>
    `${driver.firstName || ''} ${driver.lastName || ''}`.trim() ||
    driver.phone ||
    driver.email ||
    driver.id;

  const handleCloseAssignDialog = () => {
    if (isAssigning) {
      return;
    }
    resetAssignmentDialogState();
  };

  const resetDeleteDialogState = () => {
    setIsDeleteDialogOpen(false);
    setDriverToDelete(null);
    setIsDeleting(false);
  };

  const handleOpenDeleteDialog = (driver: Driver) => {
    setDriverToDelete(driver);
    setError(null);
    setSuccessMessage(null);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) {
      return;
    }
    resetDeleteDialogState();
  };

  const handleAssignDriver = async () => {
    if (!driverToAssign) {
      return;
    }

    if (!assignmentStoreId) {
      setError('Sélectionne un magasin de rattachement avant de valider.');
      return;
    }

    const driverLabel =
      `${driverToAssign.firstName || ''} ${driverToAssign.lastName || ''}`.trim() ||
      driverToAssign.phone;
    const storeLabel = selectedAssignmentStore?.name || assignmentStoreId;
    const groupLabel =
      selectedAssignmentGroup?.name ||
      selectedAssignmentStore?.operationGroupId ||
      'groupe inconnu';
    const assignmentMode: DriverAssignmentMode = lockToStore ? 'store_locked' : 'group_shared';
    const assignmentLabel = lockToStore
      ? `verrouillé sur ${storeLabel}`
      : `partagé dans ${groupLabel}`;

    try {
      setIsAssigning(true);
      setError(null);
      await driverService.assignDriverToStore(driverToAssign.id, assignmentStoreId, {
        activateNow,
        assignmentMode,
      });
      resetAssignmentDialogState();
      await loadPendingDrivers();
      if (onDriverAssigned) {
        await onDriverAssigned();
      }
      setSuccessMessage(
        activateNow
          ? `${driverLabel} a été rattaché à ${storeLabel} puis ${assignmentLabel}, et activé.`
          : `${driverLabel} a été rattaché à ${storeLabel} puis ${assignmentLabel}.`
      );
    } catch (err: any) {
      setError(err.message || 'Affectation impossible.');
      console.error('Error assigning pending driver:', err);
      setIsAssigning(false);
    }
  };

  const handleDeletePendingDriver = async () => {
    if (!driverToDelete) {
      return;
    }

    const driverLabel = getDriverLabel(driverToDelete);

    try {
      setIsDeleting(true);
      setError(null);
      const result = await driverService.deletePendingDriverRequest(driverToDelete.id);
      resetDeleteDialogState();
      await loadPendingDrivers();
      setSuccessMessage(
        result.clientAccountPreserved
          ? `${driverLabel} a été supprimé des inscriptions livreurs. Son compte client a été conservé.`
          : result.authDeleted
            ? `${driverLabel} a été supprimé des inscriptions livreurs et de Firebase Auth.`
            : `${driverLabel} a été supprimé des inscriptions livreurs. Le compte Auth était déjà absent.`
      );
    } catch (err: any) {
      setError(err.message || 'Suppression impossible.');
      console.error('Error deleting pending driver request:', err);
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {assignableStores.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Aucun magasin rattaché à un groupe opérationnel n’est disponible pour
          affecter ces inscriptions.
        </Alert>
      )}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Nom, téléphone, email, CIN..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void loadPendingDrivers()}
        >
          Actualiser
        </Button>
        <Chip
          label={`${filteredPendingDrivers.length} inscription${
            filteredPendingDrivers.length > 1 ? 's' : ''
          } sans groupe`}
          color="warning"
          variant="outlined"
        />
      </Box>

      {filteredPendingDrivers.length === 0 ? (
        <Alert severity="info">
          Aucun nouveau livreur sans groupe ne correspond aux filtres actuels.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Photo</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Inscription</TableCell>
                  <TableCell>Compte</TableCell>
                  <TableCell>Véhicule</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedPendingDrivers.map((driver) => {
                  const accountStatusMeta = getDriverAccountStatusMeta(driver);
                  const fullName =
                    `${driver.firstName || ''} ${driver.lastName || ''}`.trim() ||
                    'Livreur sans nom';

                  return (
                    <TableRow key={driver.id}>
                      <TableCell>
                        <Avatar src={driver.photoUrl} alt={fullName} />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {fullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            CIN: {driver.cin || '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{driver.phone || '—'}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {driver.email || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {driver.address || 'Adresse non renseignée'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDriverTimestamp(driver.createdAt)}</TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.75}>
                          <Chip
                            label={accountStatusMeta.label}
                            color={accountStatusMeta.color}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            approvalStatus: {driver.approvalStatus || 'pending'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {driver.vehicleType || '—'}
                        {driver.vehicleRegistration
                          ? ` (${driver.vehicleRegistration})`
                          : ''}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          gap={0.75}
                          flexWrap="nowrap"
                        >
                          <Tooltip title="Supprimer l'inscription en attente">
                            <span>
                              <IconButton
                                aria-label="Supprimer l'inscription en attente"
                                size="small"
                                onClick={() => handleOpenDeleteDialog(driver)}
                                disabled={isDeleting}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  border: '1px solid',
                                  borderColor: 'error.main',
                                  color: 'error.main',
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                  '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'error.contrastText',
                                  },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Affecter à un magasin">
                            <span>
                              <IconButton
                                aria-label="Affecter à un magasin"
                                size="small"
                                onClick={() => handleOpenAssignDialog(driver)}
                                disabled={assignableStores.length === 0}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  border: '1px solid',
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                  },
                                }}
                              >
                                <AssignmentIndIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredPendingDrivers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Inscriptions par page"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
        </>
      )}

      <Dialog
        open={isAssignDialogOpen}
        onClose={handleCloseAssignDialog}
        aria-labelledby="assign-driver-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="assign-driver-dialog-title">
          Affecter un nouveau livreur
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Le groupe opérationnel et le magasin d’attache seront déduits depuis
            le magasin sélectionné. Par défaut, le livreur reste partagé dans son
            groupe.
          </DialogContentText>

          {driverToAssign && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {`${driverToAssign.firstName || ''} ${
                  driverToAssign.lastName || ''
                }`.trim() || driverToAssign.phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {driverToAssign.phone || 'Téléphone indisponible'}
              </Typography>
            </Box>
          )}

          <TextField
            select
            fullWidth
            label="Magasin de rattachement"
            value={assignmentStoreId}
            onChange={(event) => setAssignmentStoreId(event.target.value)}
            sx={{ mb: 2 }}
          >
            {assignableStores.map((store) => {
              const groupName =
                operationGroups.find(
                  (group) => group.id === store.operationGroupId
                )?.name || store.operationGroupId;

              return (
                <MenuItem key={store.id} value={store.id}>
                  {store.name} {groupName ? `- ${groupName}` : ''}
                  {!store.isActive ? ' - inactif' : ''}
                </MenuItem>
              );
            })}
          </TextField>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label={`Groupe: ${
                selectedAssignmentGroup?.name ||
                selectedAssignmentStore?.operationGroupId ||
                'Non résolu'
              }`}
              color={selectedAssignmentGroup ? 'primary' : 'default'}
              variant="outlined"
            />
            {selectedAssignmentStore && (
              <Chip
                label={
                  selectedAssignmentStore.isActive
                    ? 'Magasin actif'
                    : 'Magasin inactif'
                }
                color={selectedAssignmentStore.isActive ? 'success' : 'warning'}
                variant="outlined"
              />
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={activateNow}
                onChange={(event) => setActivateNow(event.target.checked)}
              />
            }
            label="Activer immédiatement le compte après affectation"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={lockToStore}
                onChange={(event) => setLockToStore(event.target.checked)}
              />
            }
            label="Verrouiller ce livreur sur le magasin sélectionné"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} disabled={isAssigning}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleAssignDriver()}
            disabled={
              isAssigning ||
              !assignmentStoreId ||
              !selectedAssignmentStore?.operationGroupId
            }
            startIcon={isAssigning ? <CircularProgress size={18} /> : <AssignmentIndIcon />}
          >
            {isAssigning ? 'Affectation...' : 'Affecter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-pending-driver-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-pending-driver-dialog-title">
          Supprimer l’inscription en attente
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Cette action supprime le document livreur en attente. Avant de
            supprimer Firebase Auth, le backend vérifie si un compte client
            existe pour le même UID.
          </DialogContentText>

          {driverToDelete && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {getDriverLabel(driverToDelete)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {driverToDelete.phone || 'Téléphone indisponible'}
                {driverToDelete.email ? ` · ${driverToDelete.email}` : ''}
              </Typography>
            </Box>
          )}

          <Alert severity="warning">
            Si un compte client existe, il sera conservé. Sinon, le compte Auth
            sera supprimé aussi pour libérer le téléphone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDeletePendingDriver()}
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={18} /> : <DeleteOutlineIcon />
            }
          >
            {isDeleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingDriverRequestsPanel;
